import { ToJSON } from './json';
import { IsEqualTo } from './IsEqualTo';
import { CampaignId, ResourceName } from './googleAds/domain';
import { USD } from './money';
import { JSONableError } from './error';
import { hash, List, ValueObject } from 'immutable';
import { hashMerge } from './immutablejs.extensions';

export class ApiKey implements ToJSON, IsEqualTo, ValueObject {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _0c82e13cae8f45a49571cf2958212eb8_Brand = 'Prevent structural compatibility';

  constructor(public readonly value: string) {}

  isEqualTo(other: ApiKey | undefined): boolean {
    return this.equals(other);
  }

  toJSON(): any {
    return this.toString();
  }

  toString(): string {
    return this.value;
  }

  equals(other: unknown): boolean {
    return other instanceof ApiKey && this.value === other.value;
  }

  hashCode(): number {
    return hash(this.value);
  }
}

export class ClientKey {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _3a0bce6391434677a0894ed60b3ebb91_Brand = 'Prevent structural compatibility';

  constructor(public readonly value: string) {}

  isEqualTo(other: ClientKey): boolean {
    return this.value === other.value;
  }
}

export class ClientMnemonic implements ValueObject {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _bb8af9add9314529b34777ecdf567955_Brand = 'Prevent structural compatibility';

  constructor(public readonly value: string) {}

  equals(other: unknown): boolean {
    return other instanceof ClientMnemonic && this.value === other.value;
  }

  hashCode(): number {
    return hash(this.value);
  }

  isEqualTo(other: ClientMnemonic): boolean {
    return this.value === other.value;
  }

  toJSON(): any {
    return this.toString();
  }

  toString(): string {
    return this.value;
  }
}

export class BookingReference implements ToJSON, IsEqualTo, ValueObject {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _f9f7eb6f9bb94a4e8ca875ba40eaaccf_Brand = 'Prevent structural compatibility';

  constructor(public readonly value: string) {}

  isEqualTo(other: BookingReference | undefined): boolean {
    return other != undefined && this.value === other.value;
  }

  toJSON(): any {
    return this.value;
  }

  equals(other: unknown): boolean {
    return other instanceof BookingReference && this.value === other.value;
  }

  hashCode(): number {
    return hash(this.value);
  }
}

export class OldHotelBookingId implements ToJSON, IsEqualTo, ValueObject {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _b4096d00a3aa4ab98c8f052416b568cc_Brand = 'Prevent structural compatibility';

  constructor(
    public readonly apiKey: ApiKey,
    public readonly bookingReference: BookingReference,
  ) {}

  isEqualTo(other: OldHotelBookingId | undefined): boolean {
    return other != undefined && this.apiKey.isEqualTo(other.apiKey) && this.bookingReference.isEqualTo(other.bookingReference);
  }

  toJSON() {
    return {
      name: OldHotelBookingId.name,
      apiKey: this.apiKey,
      bookingReference: this.bookingReference,
    };
  }

  toString() {
    return `${this.apiKey.value}:${this.bookingReference.value}`;
  }

  equals(other: unknown): boolean {
    return other instanceof OldHotelBookingId && this.apiKey.equals(other.apiKey) && this.bookingReference.equals(other.bookingReference);
  }

  hashCode(): number {
    return hashMerge(this.apiKey.hashCode(), this.bookingReference.hashCode());
  }
}

export class GoogleAccountId implements ValueObject, IsEqualTo, ToJSON {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _a716b693e1214adabb04dcb30ee49cf2_Brand = 'Prevent structural compatibility';

  constructor(initialValue: string) {
    const undashedValue = this.toValueWithoutDashesHelper(initialValue);
    if (!undashedValue) throw new GoogleAccountId.InvalidGoogleAccountId('undefined');
    if (undashedValue.length != 10) throw new GoogleAccountId.InvalidGoogleAccountId(undashedValue);
    if (!/^[0-9]{10}$/.test(undashedValue)) {
      throw new GoogleAccountId.InvalidGoogleAccountId(undashedValue);
    }
    this.value = undashedValue;
  }

  equals(other: unknown): boolean {
    return this.isEqualTo(other as this);
  }

  hashCode(): number {
    return hash(this.toValueWithoutDashes());
  }

  toJSON() {
    return {
      name: GoogleAccountId.name,
      value: this.value,
    };
  }

  public readonly value: string;

  isEqualTo(other: this | undefined): boolean {
    return this.toValueWithoutDashes() === other?.toValueWithoutDashes();
  }

  toValueWithoutDashes(): string {
    return this.toValueWithoutDashesHelper(this.value);
  }

  toValueWithDashes() {
    const valueWithoutDashes = this.toValueWithoutDashes();
    return `${valueWithoutDashes.substring(0, 3)}-${valueWithoutDashes.substring(3, 6)}-${valueWithoutDashes.substring(6)}`;
  }

  private toValueWithoutDashesHelper(originalValue: string) {
    return originalValue.replaceAll('-', '');
  }
}

export namespace GoogleAccountId {
  export class InvalidGoogleAccountId extends JSONableError {
    // @ts-ignore
    private readonly brand: unique symbol = Symbol();

    constructor(public readonly value: string) {
      super(`${value} is not a valid Google account ID`);
    }

    isEqualTo(other: InvalidGoogleAccountId): boolean {
      return this.value === other.value;
    }

    toJSON() {
      return {
        ...super.toJSON(),
        googleAccountId: this.value,
      };
    }
  }
}

export class GoogleAdsApiQuery {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _af4e36954345440da6154f04de9796e0_Brand = 'Prevent structural compatibility';

  constructor(public readonly value: string) {}

  isEqualTo(other: GoogleAdsApiQuery): boolean {
    return this.value === other.value;
  }
}

export class GTagId implements ToJSON, IsEqualTo, ValueObject {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _979a09bf2f544fceae01b16d948569c6_Brand = 'Prevent structural compatibility';

  constructor(public readonly value: string) {}

  equals(other: unknown): boolean {
    return other instanceof GTagId && this.value === other.value;
  }

  hashCode(): number {
    return hash(this.value);
  }

  isEqualTo(other: GTagId | undefined): boolean {
    return other != undefined && this.value === other.value;
  }

  toString(): string {
    return this.value;
  }

  toJSON() {
    return {
      name: GTagId.name,
      value: this.value,
    };
  }
}

export class User {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _2bcafce054a547be9d9a45d347cc7ea2_Brand = 'Prevent structural compatibility';

  constructor(public readonly value: string) {}

  isEqualTo(other: User): boolean {
    return this.value === other.value;
  }
}

export enum KeywordType {
  Brand = 'branded',
  NonBrand = 'non_branded',
  PMax = 'pmax',
  DisplayRetargeting = 'display_rt',
  NonRoom = 'non_room',
  DynamicSearchAd = 'dynamic_search_ad',
}

export function parseKeywordType(input: string): KeywordType {
  switch (input.toLowerCase()) {
    case KeywordType.Brand:
      return KeywordType.Brand;
    case KeywordType.NonBrand:
      return KeywordType.NonBrand;
    case KeywordType.PMax:
      return KeywordType.PMax;
    case KeywordType.DisplayRetargeting:
      return KeywordType.DisplayRetargeting;
    case KeywordType.NonRoom:
      return KeywordType.NonRoom;
    case KeywordType.DynamicSearchAd:
      return KeywordType.DynamicSearchAd;
    default:
      return KeywordType.NonBrand;
  }
}

export class KeywordId implements ToJSON, IsEqualTo {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _52cf579141bb4b6793db0107687d4bfb_Brand = 'Prevent structural compatibility';

  constructor(public readonly value: string) {}

  isEqualTo(other: KeywordId | undefined): boolean {
    return other != undefined && this.value === other.value;
  }

  toJSON() {
    return {
      name: KeywordId.name,
      value: this.value,
    };
  }
}

export class KeywordResourceName implements ToJSON, IsEqualTo, ValueObject {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _e6c55c42b8744488afc864a09a5b1dd5_Brand = 'Prevent structural compatibility';

  constructor(public readonly value: string) {}

  equals(other: unknown): boolean {
    return Boolean(other && other instanceof KeywordResourceName && this.value === other.value);
  }

  hashCode(): number {
    return hash(this.value);
  }

  isEqualTo(other: KeywordResourceName | undefined): boolean {
    return this.equals(other);
  }

  toJSON() {
    return {
      name: KeywordResourceName.name,
      value: this.value,
    };
  }
}

export class Keyword implements ToJSON, IsEqualTo, ValueObject {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _ee47fd5648174913aa3d2f5c3091e9e9_Brand = 'Prevent structural compatibility';

  constructor(
    public readonly resourceName: KeywordResourceName,
    public readonly value: string,
    public readonly campaignId: CampaignId,
    public readonly keywordType: KeywordType,
  ) {}

  equals(other: unknown): boolean {
    return Boolean(
      other &&
        other instanceof Keyword &&
        this.resourceName.equals(other.resourceName) &&
        this.value === other.value &&
        this.campaignId.equals(other.campaignId) &&
        this.keywordType === other.keywordType,
    );
  }

  hashCode(): number {
    return List.of(this.resourceName.hashCode(), hash(this.value), this.campaignId.hashCode(), hash(this.keywordType)).hashCode();
  }

  isEqualTo(other: Keyword | undefined): boolean {
    return this.equals(other);
  }

  toJSON() {
    return {
      name: Keyword.name,
      resourceName: this.resourceName.value,
      value: this.value,
      campaignId: this.campaignId.value,
      keywordType: this.keywordType,
    };
  }

  toString() {
    return JSON.stringify(this.toJSON());
  }
}

export class GClId implements ToJSON, IsEqualTo {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _f53cc39c0c304cb19d122592763f83ca_Brand = 'Prevent structural compatibility';

  constructor(public readonly value: string) {}

  isEqualTo(other: GClId | undefined): boolean {
    return other != undefined && this.value === other.value;
  }

  toJSON() {
    return this.value;
  }
}

export class Cpc implements ToJSON, IsEqualTo {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _d36a121e881e493bb3eb30f110c9fda4_Brand = 'Prevent structural compatibility';

  constructor(public readonly value: USD) {
    const amount = value.amount;

    if (amount.isGreaterThan('1000')) throw new Error(`CPC must be at most 1000.00, but was ${amount}`);
    if (amount.isLessThan('0.01')) throw new Error(`CPC must be at least 0.01, but was ${amount}`);
    if (!amount.decimalPlaces(2).isEqualTo(amount)) throw new Error(`CPC must be a whole multiple of 0.01, but was ${amount}`);
  }

  isEqualTo(other: Cpc | undefined): boolean {
    return other != undefined && this.value.isEqualTo(other.value);
  }

  toJSON() {
    return {
      name: Cpc.name,
      value: this.value,
    };
  }
}

export class HotelName implements IsEqualTo {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _73d837369f584b139b940358ef1f09d0_Brand = 'Prevent structural compatibility';

  public readonly value: string;

  constructor(value: string) {
    this.value = value.trim();
  }

  isEqualTo(other: HotelName | undefined): boolean {
    return this.value === other?.value;
  }

  toString(): string {
    return this.value;
  }

  trimmedValue(): string {
    return this.value.trim();
  }
}

export class BookingId implements ToJSON, IsEqualTo, ValueObject {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _73d837369f584b139a940358af19999_Brand = 'Prevent structural compatibility';

  constructor(public readonly value: string) {}

  toJSON() {
    return this.value;
  }

  equals(other: unknown): boolean {
    return other instanceof BookingId && this.value === other.value;
  }

  hashCode(): number {
    return hash(this.value);
  }

  isEqualTo(other: BookingId | undefined): boolean {
    return other instanceof BookingId && this.value === other.value;
  }

  toString(): string {
    return this.value;
  }
}

export class GoogleAdCampaign implements ValueObject {
  constructor(
    readonly googleAccount: GoogleAccountId,
    readonly resourceName: ResourceName,
  ) {}

  equals(other: unknown): boolean {
    return (
      other instanceof GoogleAdCampaign && this.googleAccount.equals(other.googleAccount) && this.resourceName.equals(other.resourceName)
    );
  }

  hashCode(): number {
    return List.of([this.googleAccount.hashCode(), this.resourceName.hashCode()]).hashCode();
  }
}
